import { CSSProperties, ComponentType, ForwardedRef } from 'react';

import { SizeProps, SpacingProps, mapStyledProps } from '../../styles';
import { DefaultComponentProps } from '../../types';
import { cn } from '../../utils';

import BodyRow from './components/BodyRow/BodyRow';
import EmptyBodyRow from './components/EmptyBodyRow';
import HeaderRow from './components/HeaderRow';
import { DataTableClassNames } from './constants';
import { Spinner, Table, TableScrollArea } from './DataTable.styled';
import useSortedTableData from './hooks/useSortedTableData';
import {
  ColumnDef,
  DataTableBlurHandler,
  DataTableKeySelector,
  DataTableRowActionProps,
  DataTableRowStripColorSelector,
  DataTableSortDirection,
  DataTableSortEventHandler,
} from './types';

export type DataTableProps<TData> = {
  isLoading?: boolean;
  columns: ColumnDef<TData>[];
  data: TData[];

  /**
   * Specifies value to be used as `key` for table rows (as required by react when rendering array of children)
   */
  keySelector: DataTableKeySelector<TData>;
  onCellBlur?: DataTableBlurHandler<TData>;

  /**
   * You can use this function to define row strip color
   */
  rowStripColorSelector?: DataTableRowStripColorSelector<TData>;

  /**
   * Invoked when some header cell sort button is clicked, you can use this field in case you want to have controlled sort or similar
   *
   * @note
   * If you want to prevent default sort for some column you can specify `sort: "controlled"` in column definition
   */
  onSort?: DataTableSortEventHandler<TData>;
  initialSortKey?: keyof TData;
  initialSortDirection?: DataTableSortDirection;
  rowAction?: ComponentType<DataTableRowActionProps<TData>>;
  layout?: CSSProperties['tableLayout'];

  /**
   * @note
   * `React.forwardRef` messes up the implicit type inference of data,
   * so if you want to pass ref to it you can use this prop as workaround until we migrate to React v19
   */
  forwardedRef?: ForwardedRef<HTMLDivElement>;
  style?: CSSProperties;
} & DefaultComponentProps &
  SizeProps &
  SpacingProps;

const DataTable = <TData extends Record<string, unknown>>({
  columns,
  data,
  isLoading = false,
  keySelector,
  rowStripColorSelector,
  onCellBlur,
  onSort,
  initialSortKey,
  initialSortDirection = 'asc',
  rowAction,
  layout,
  forwardedRef,
  className,
  style,
  p,
  pt,
  pr,
  pb,
  pl,
  ...passThroughProps
}: DataTableProps<TData>) => {
  const { sortedData, sortKey, sortDirection, handleSort } = useSortedTableData(
    {
      data,
      columns,
      initialSortKey,
      initialSortDirection,
      onSort,
    },
  );

  const hasRowAction = !!rowAction;

  const renderColGroup = () => (
    <colgroup>
      {columns.map(column => (
        <col
          aria-label={column.ariaLabel}
          className={cn(`col-${column.key.toString()}`, column.className)}
          key={column.key.toString()}
          style={column.style}
        />
      ))}
      {hasRowAction && (
        <col
          className={DataTableClassNames.Action}
          key={DataTableClassNames.Action}
        />
      )}
    </colgroup>
  );

  const renderBodyRows = () => {
    if (!sortedData.length) {
      return <EmptyBodyRow columns={columns} hasRowAction={hasRowAction} />;
    }

    return sortedData.map(item => {
      const rowKey = keySelector(item);

      return (
        <BodyRow
          columns={columns}
          item={item}
          key={rowKey}
          rowAction={rowAction}
          rowKey={rowKey}
          rowStripColorSelector={rowStripColorSelector}
          onCellBlur={onCellBlur}
        />
      );
    });
  };

  const mappedProps = mapStyledProps(passThroughProps);

  return (
    <TableScrollArea
      className={className}
      ref={forwardedRef}
      style={style}
      {...mappedProps}>
      <Table
        $isLoading={isLoading}
        $layout={layout}
        $p={p}
        $pb={pb}
        $pl={pl}
        $pr={pr}
        $pt={pt}>
        {renderColGroup()}
        <thead>
          <HeaderRow
            columns={columns}
            hasRowAction={!!rowAction}
            sortDirection={sortDirection}
            sortKey={sortKey}
            onSort={handleSort}
          />
        </thead>
        <tbody>{renderBodyRows()}</tbody>
      </Table>
      {isLoading && <Spinner />}
    </TableScrollArea>
  );
};

export default DataTable;
